/**
 * Crash recovery for the draft editor.
 *
 * While a draft has unsaved edits, its bytes are checkpointed here every few
 * seconds, when the tab is hidden, and when the editor unmounts. Reopening the
 * draft offers the checkpoint back; a successful save removes it. IndexedDB
 * rather than localStorage: documents are megabytes and localStorage's quota
 * is small and synchronous.
 *
 * One ordered queue serialises every operation, so a slow checkpoint can never
 * land after the save that made it obsolete and resurrect a stale draft.
 */
export type DocxRecovery = {
  key: string;
  buffer: ArrayBuffer;
  /** The server token the edits were made on top of; null when unknown. */
  baseUpdatedAt: string | null;
  savedAt: number;
};

const DB_NAME = "caseblink-docx-recovery";
const DB_VERSION = 1;
const DRAFTS = "drafts";
const VERSIONS = "versions";

let queue: Promise<unknown> = Promise.resolve();

const available = () => typeof indexedDB !== "undefined";

const database = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DRAFTS))
        db.createObjectStore(DRAFTS, { keyPath: "key" });
      if (!db.objectStoreNames.contains(VERSIONS))
        db.createObjectStore(VERSIONS, { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const operation = <T>(
  run: (store: IDBObjectStore) => IDBRequest<T>,
  storeName: string = DRAFTS
): Promise<T> => {
  if (!available()) return Promise.reject(new Error("IndexedDB unavailable"));
  const next = queue
    .catch(() => undefined)
    .then(async () => {
      const db = await database();
      return new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(storeName, "readwrite");
        const request = run(transaction.objectStore(storeName));
        transaction.oncomplete = () => {
          db.close();
          resolve(request.result);
        };
        transaction.onabort = () => {
          db.close();
          reject(transaction.error ?? new Error("Draft recovery failed"));
        };
        transaction.onerror = () => {
          // onabort reports the failure.
        };
      });
    });
  queue = next;
  return next;
};

const isRecovery = (value: unknown, key: string): value is DocxRecovery =>
  typeof value === "object" &&
  value !== null &&
  (value as DocxRecovery).key === key &&
  (value as DocxRecovery).buffer instanceof ArrayBuffer &&
  typeof (value as DocxRecovery).savedAt === "number" &&
  ((value as DocxRecovery).baseUpdatedAt === null ||
    typeof (value as DocxRecovery).baseUpdatedAt === "string");

export const readDocxRecovery = async (
  key: string
): Promise<DocxRecovery | null> => {
  if (!available()) return null;
  try {
    const value: unknown = await operation((store) => store.get(key));
    return isRecovery(value, key) ? value : null;
  } catch {
    return null;
  }
};

export const writeDocxRecovery = async (draft: DocxRecovery) => {
  if (!available()) return;
  try {
    await operation((store) => store.put(draft));
  } catch {
    // Recovery is best effort; the editor still holds the bytes.
  }
};

export const removeDocxRecovery = async (key: string) => {
  if (!available()) return;
  try {
    await operation((store) => store.delete(key));
  } catch {
    // Nothing to do: a stale checkpoint is offered and declined next open.
  }
};

export type DocxVersion = { savedAt: number; buffer: ArrayBuffer };

const versionsOf = (value: unknown): DocxVersion[] => {
  if (typeof value !== "object" || value === null) return [];
  const list = (value as { versions?: unknown }).versions;
  if (!Array.isArray(list)) return [];
  return list.filter(
    (item): item is DocxVersion =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as DocxVersion).savedAt === "number" &&
      (item as DocxVersion).buffer instanceof ArrayBuffer
  );
};

/** The last few bytes this device saved for a draft, newest first. */
export const readDocxVersions = async (key: string): Promise<DocxVersion[]> => {
  if (!available()) return [];
  try {
    return versionsOf(await operation((store) => store.get(key), VERSIONS));
  } catch {
    return [];
  }
};

const KEPT_VERSIONS = 5;

/** Remember what was just saved, so a bad save can be undone from this device. */
export const keepDocxVersion = async (key: string, buffer: ArrayBuffer) => {
  if (!available()) return;
  try {
    // Read and append inside one transaction so two windows cannot drop one.
    await operation((store) => {
      const request = store.get(key);
      request.onsuccess = () => {
        store.put({
          key,
          versions: [
            { savedAt: Date.now(), buffer },
            ...versionsOf(request.result)
          ].slice(0, KEPT_VERSIONS)
        });
      };
      return request;
    }, VERSIONS);
  } catch {
    // Best effort.
  }
};

/** The recovery key for a draft. */
export const draftRecoveryKey = (draftId: string) => `draft:${draftId}`;

/**
 * The recovery key for a case file open in the Word, Excel or PowerPoint
 * editor. Same store, same rules: the bytes are whatever the editor
 * serialises, so a workbook checkpoints here as readily as a docx.
 */
export const fileRecoveryKey = (nodeId: string) => `file:${nodeId}`;
